/**
 * @file orders-empty.tsx
 * @description Тексты пустого реестра заказов (phase19.31, разбор A §3 (а)).
 *
 * Пустота бывает разной, и человеку нужен разный совет: заказов нет вовсе,
 * нет «моего хода», номер не найден, ничего под набором фильтров. Тексты —
 * данными по виду отбора (`ordersFilterKind`), а не ветками в странице.
 */

import { Search, ShoppingCart, UserRound } from "lucide-react";
import { Link } from "react-router";

import { Button } from "../../ui/button";
import type { ListEmptySpec, ListFilteredEmptySpec } from "../../ui/responsive-list-types";
import type { OrdersFilterKind, OrdersRegistryQuery } from "../../../lib/order-registry-query";

/** Заказов нет совсем: без права на создание — без второй половины фразы. */
export function ordersEmptySpec(newOrderHref: string | null): ListEmptySpec {
  return {
    icon: ShoppingCart,
    title: "Заказов пока нет",
    description: newOrderHref
      ? "Они появятся, когда покупатель оформит заказ на сайте, — или создайте заказ вручную"
      : "Они появятся, когда покупатель оформит заказ на сайте",
    action: newOrderHref ? (
      <Button variant="outline" asChild>
        <Link to={newOrderHref}>Новый заказ</Link>
      </Button>
    ) : undefined,
  };
}

interface FilteredInput {
  kind: OrdersFilterKind;
  query: OrdersRegistryQuery;
  /** «Все заказы» — снять «Мой ход». */
  onShowAll: () => void;
  onReset: () => void;
}

/** Пусто под отбором: текст и кнопка сброса по виду отбора. */
export function ordersFilteredSpec({ kind, query, onShowAll, onReset }: FilteredInput): ListFilteredEmptySpec {
  if (kind === "owner") {
    return { icon: UserRound, title: "Сейчас нет заказов, где нужен ваш ход", onReset: onShowAll, resetLabel: "Все заказы" };
  }
  if (kind === "search") {
    return {
      icon: Search,
      title: `Заказ «${query.search}» не найден`,
      description: "Поиск идёт по номеру заказа",
      onReset,
      resetLabel: "Сбросить поиск",
    };
  }
  return { icon: Search, title: "Нет заказов по выбранным условиям", onReset };
}
